<template>
  <div class="c-feed-list">
    <div v-if="feeds.length > 0" class="c-feed-list__items">
      <component
        :is="feedListComponent(feed.integration.type)"
        v-for="feed in feeds"
        :key="'integration_' + feed.id"
        :feed="feed"
      >
        <template #left-content="{ noGrow, multipleSources }">
          <div
            class="flex items-center gap-8"
            :class="{
              'flex-1': !noGrow,
              'ml-8': multipleSources,
            }"
          >
            <BittsSvg class="w-24 h-24" :svg="feed.integration.type + 'Icon'" />
            <div class="font-bold align-center text-neutral-900 text-m">
              <span>{{ friendlyNameFor(feed) }}</span>
            </div>
          </div>
        </template>
        <template #status="{ text, iconClass }">
          <span :class="iconClass" class="status-circle" />
          <span class="mr-8 text-neutral-500 text-base">
            {{ text }}
          </span>
        </template>
        <template #feed-deletion-failed>
          <BittsAlert
            class="mb-12 mx-12"
            message="We were unable to delete your data source"
            description="Try again or contact"
            color="warning"
          >
            <template #link>
              <a target="_blank" href="mailto:support@crossbeam.com"
                >support@crossbeam.com</a
              >
            </template>
            .
          </BittsAlert>
        </template>
        <template #feed-error>
          <FeedListItemError :feed="feed" class="mx-12" />
        </template>
        <template
          v-if="hasPermission('manage:data-sources')"
          #feed-settings-button="{ isGeneralSettings }"
        >
          <BittsButton
            :center-icon="['fak', 'settings']"
            type="neutral"
            variant="outline"
            size="x-small"
            aria-label="Settings"
            @click="() => goToSettings(feed, isGeneralSettings)"
          />
        </template>
      </component>
    </div>
    <div v-else class="background-transparent">
      <div class="c-feed-list__empty-state-title">
        No data sources connected
      </div>
      <div
        v-if="hasPermission('manage:data-sources')"
        class="c-feed-list__empty-state-subtitle"
      >
        Choose a data source to connect from the list below.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAlert, BittsButton, BittsSvg } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import CsvFeed from '@/components/data-sources/CsvFeed.vue';
import FeedListItemError from '@/components/data-sources/FeedListItemError.vue';
import GenericFeed from '@/components/data-sources/GenericFeed.vue';
import GoogleSheetsFeed from '@/components/data-sources/GoogleSheetsFeed.vue';
import WarehouseFeed from '@/components/data-sources/WarehouseFeed.vue';

import useAuth from '@/composables/useAuth';
import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import useFeedsDeleting from '@/composables/useFeedsDeleting';
import {
  AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE,
  DELL_SPIL_DATA_SOURCE_TYPE,
  EXTERNAL_FILE_DATA_SOURCE_TYPE,
  getDataSourceLabel,
} from '@/constants/data_sources';
import { PERMANENT_FLAGS } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useFeatureFlagStore,
  useFeedsStore,
  useFileUploadsStore,
} from '@/stores';
import { Feed, FeedIntegrationType } from '@/types/feeds';

import { DataSourceSettingsTabType } from './SettingsModal/constants';

const router = useRouter();

const feedsStore = useFeedsStore();
const fileUploadsStore = useFileUploadsStore();
const { hasFeatureFlag } = useFeatureFlagStore();

const { hasPermission } = useAuth();
const { feeds: ownFeeds } = storeToRefs(feedsStore);
const { awsFeeds, refreshAwsPartnerCentral } = useAwsPartnerCentral();

// The AWS Partner Central feed lives on an offline-partner org and is kept out
// of FeedsStore (so it can't leak into own-org consumers like the population
// builder); surface it here in the data-sources list only.
const feeds = computed(() => [...ownFeeds.value, ...awsFeeds.value]);

onMounted(async () => {
  await Promise.all([
    fileUploadsStore.refreshFileUploadsStore(),
    // refreshAwsPartnerCentral throws on failure; on the initial list load,
    // capture it so the rest of the data-sources list still renders.
    refreshAwsPartnerCentral().catch(captureException),
  ]);
});

/* Poll feeds if they go into a deleting state */
useFeedsDeleting();

function friendlyNameFor(feed: Feed) {
  return getDataSourceLabel(
    feed.integration.type,
    feed.integration.friendly_name,
  );
}

async function goToSettings(feed: Feed, isGeneralSettings?: boolean) {
  if (isGeneralSettings) {
    await router.push({
      name: 'gsheet-general-settings',
      params: { id: feed.id },
    });
    return;
  }

  await router.push({
    name: 'data-sources-settings-new',
    params: { id: feed.id, tab: DataSourceSettingsTabType.GENERAL },
  });
}

type FeedComponent =
  | typeof GoogleSheetsFeed
  | typeof CsvFeed
  | typeof GenericFeed
  | typeof WarehouseFeed;

function feedListComponent(
  type: FeedIntegrationType,
): FeedComponent | undefined {
  if (type === DELL_SPIL_DATA_SOURCE_TYPE) {
    return hasFeatureFlag(PERMANENT_FLAGS.DELL_SPIL_FEED)
      ? GenericFeed
      : undefined;
  }
  if (type === EXTERNAL_FILE_DATA_SOURCE_TYPE) {
    return hasFeatureFlag('external_file_data') ? GenericFeed : undefined;
  }
  // `aws_partner_central` is not yet in the generated OpenAPI feed-type union,
  // so it can't be a typed key in the map below — match it up front. Its feed
  // carries an account source, so it renders like any other CRM feed.
  if ((type as string) === AWS_PARTNER_CENTRAL_DATA_SOURCE_TYPE) {
    return GenericFeed;
  }
  type FeedTypeExcludingDellSpil = Exclude<
    FeedIntegrationType,
    typeof DELL_SPIL_DATA_SOURCE_TYPE
  >;
  const feedlistItemMap: Partial<
    Record<FeedTypeExcludingDellSpil, FeedComponent>
  > = {
    google_sheets: GoogleSheetsFeed,
    file_upload: CsvFeed,
    hubspot_v3: GenericFeed,
    pipedrive: GenericFeed,
    snowflake: WarehouseFeed,
    salesforce: GenericFeed,
    microsoft_dynamics: GenericFeed,
    databricks: WarehouseFeed,
  };
  return feedlistItemMap[type as FeedTypeExcludingDellSpil];
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-feed-list {
  @apply flex flex-col text-base mb-32;
}

.c-feed-list__empty-state-title {
  @apply mb-8 text-lg text-center text-neutral-500;
}

.c-feed-list__empty-state-subtitle {
  @apply text-sm text-center text-neutral-500 pb-24;
}

.status-circle {
  @apply w-8 h-8 rounded-3 inline-block;
}

/* Styles for settings elipses */
.overflow-item {
  @apply cursor-pointer px-16 py-10;
  &:hover {
    @apply bg-primary-background-weak;
  }
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
.c-feed-list {
  .c-feed-list__feed {
    @apply bg-white overflow-auto border-neutral-border shadow-component border-l border-r border-t;

    &:first-of-type {
      @apply rounded-t-8;
    }

    &:last-of-type {
      @apply rounded-b-8;
    }

    &:last-child {
      @apply border-b;
    }
  }
}
</style>
